/**
 * TaskRatings.js
 *
 * Star ratings (1-6) on a task, its subtasks and its daily reports: whether
 * the work actually solved the problem.
 *
 * Every rule lives in ratings_api.php - who may rate, no rating your own work,
 * tasks and subtasks only once finished, a reason for 1 or 2 stars. This file
 * only draws what the server says each person may do. The one rule repeated
 * here (a reason for a low rating) is repeated so the person hears about it
 * before pressing Save, not instead of the server checking it.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Stack,
  Button,
  Typography,
  Tooltip,
  TextField,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Avatar,
  IconButton,
  Rating,
  CircularProgress,
  alpha,
} from "@mui/material";
import axios from "axios";

import { useAuth } from "../../AuthContext";

import { SectionCard, fmtDate, initials, STAFF_IMAGE_BASE } from "./taskShared";

import StarRoundedIcon from "@mui/icons-material/StarRounded";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";

export const RATINGS_URL = "https://mindsinaction.com.na/api/mindsbackend/ratings_api.php";

export const STAR = "#e0a800";

/** Never throws: always { success, message, data }. */
const ratingsCall = async (action, fields = {}) => {
  const form = new FormData();
  form.append("action", action);
  form.append("token", process.env.REACT_APP_API_TOKEN);
  Object.entries(fields).forEach(([k, v]) => {
    if (v !== undefined && v !== null) form.append(k, String(v));
  });
  try {
    const res = await axios.post(RATINGS_URL, form, { withCredentials: true });
    return res?.data ?? { success: false, message: "No response from the server" };
  } catch (err) {
    const body = err?.response?.data;
    if (body && typeof body === "object") return body;
    return { success: false, message: err?.message || "Request failed" };
  }
};

/**
 * Staff photos by id, loaded once per page and shared by every rating row.
 *
 * The rating endpoints send each rater's photo, but a row must not lose it
 * when they do not - an older ratings_api.php on the server, or a rating from
 * somebody whose photo was added later. A failed load is forgotten so the next
 * row can try again.
 */
let photoDirectory = null;
const loadPhotos = (getData) => {
  if (!photoDirectory) {
    photoDirectory = Promise.resolve(getData("proccess.php", "staffs"))
      .then((res) => {
        if (!res?.success || !Array.isArray(res.data)) {
          photoDirectory = null;
          return {};
        }
        const byId = {};
        res.data.forEach((st) => {
          if (st?.image) byId[String(st.Id ?? st.id)] = st.image;
        });
        return byId;
      })
      .catch(() => {
        photoDirectory = null;
        return {};
      });
  }
  return photoDirectory;
};

/** The photo the server sent, or else this person's photo from the staff list. */
const useStaffPhoto = (staffId, given) => {
  const { getData } = useAuth();
  const [looked, setLooked] = useState("");
  useEffect(() => {
    if (given || !staffId || !getData) return undefined;
    let live = true;
    loadPhotos(getData).then((byId) => {
      if (live) setLooked(byId[String(staffId)] || "");
    });
    return () => {
      live = false;
    };
  }, [staffId, given, getData]);
  return given || looked;
};

/** Safari will not parse "2026-10-08 14:33:00"; give it the T. */
const when = (v) => (v ? fmtDate(String(v).replace(" ", "T")) : "");

/* ================================================================== */
/*  Data                                                               */
/* ================================================================== */

/**
 * All the ratings for one task page in one request. `refreshKey` is anything
 * that changes when the task reloads - a subtask being finished changes what
 * may be rated, so the ratings reload with it.
 */
export const useTaskRatings = (taskId, refreshKey) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  // Only the latest load may land.
  const seq = useRef(0);

  const load = useCallback(async () => {
    if (!taskId) return;
    const mine = ++seq.current;
    const res = await ratingsCall("list", { task_id: taskId });
    if (mine !== seq.current) return;
    if (!res?.success) {
      setError(res?.message || "Could not load ratings");
      return;
    }
    setError("");
    setData(res.data);
  }, [taskId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const rate = useCallback(
    async (itemType, itemId, stars, comment) => {
      const res = await ratingsCall("rate", { item_type: itemType, item_id: itemId, stars, comment });
      if (res?.success) await load();
      return res;
    },
    [load]
  );

  return {
    loaded: !!data,
    error,
    item: (type, id) => data?.items?.[`${type}:${id}`] || null,
    meanings: data?.meanings || {},
    max: data?.max_stars || 6,
    rate,
    reload: load,
  };
};

/* ================================================================== */
/*  Small inline summary - for subtasks and daily reports              */
/* ================================================================== */

/**
 * Stars plus "4.0 · 2 ratings", or a small "Rate" link if nothing is rated yet
 * and this person may rate it. Draws nothing at all when there is nothing to
 * show and nothing to do, so rows do not fill up with empty stars.
 */
export const StarsInline = ({ ui, summary, onOpen }) => {
  if (!summary) return null;
  const { count, average, can_rate: canRate } = summary;
  if (!count && !canRate) return null;

  return (
    <Tooltip title={count ? "See ratings" : "Rate this"}>
      <Box
        component="button"
        type="button"
        onClick={onOpen}
        aria-label={count ? `Rated ${average} of 6 by ${count} ${count === 1 ? "person" : "people"}` : "Rate this"}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.6,
          border: `1px solid ${ui.border}`,
          borderRadius: "999px",
          bgcolor: ui.surface,
          px: 1,
          py: 0.25,
          cursor: "pointer",
          flexShrink: 0,
          font: "inherit",
          "&:hover": { borderColor: alpha(STAR, 0.7) },
        }}
      >
        {count ? (
          <>
            <StarRoundedIcon sx={{ fontSize: 16, color: STAR }} />
            <Typography variant="caption" fontWeight={800} color={ui.textPrimary}>
              {Number(average).toFixed(1)}
            </Typography>
            <Typography variant="caption" color={ui.textSecondary}>
              ({count})
            </Typography>
          </>
        ) : (
          <>
            <StarBorderRoundedIcon sx={{ fontSize: 16, color: ui.textSecondary }} />
            <Typography variant="caption" fontWeight={700} color={ui.textSecondary}>
              Rate
            </Typography>
          </>
        )}
      </Box>
    </Tooltip>
  );
};

/* ================================================================== */
/*  Report pieces                                                      */
/* ================================================================== */

/** In what capacity someone rated - the server decides, this only names it. */
const CAPACITY = {
  top: "Top management",
  leader: "Leader",
  supervisor: "Supervisor",
  former: "No longer on the task",
};

/** Filled stars in the star colour; empty ones visible on light and dark surfaces. */
const starSx = (ui, extra = {}) => ({
  color: STAR,
  "& .MuiRating-iconEmpty": { color: alpha(ui.textSecondary, 0.45) },
  ...extra,
});

const avg = (list) =>
  list.length ? Math.round((list.reduce((n, r) => n + r.stars, 0) / list.length) * 10) / 10 : null;

/**
 * How many people gave each number of stars, 6 down to 1.
 *
 * One measure, so one colour and no legend; counts sit at the end of each bar
 * in ordinary text colour, and an empty row still shows its 0 so the shape of
 * the spread is honest.
 */
const Breakdown = ({ ui, ratings, max }) => {
  const counts = Array.from({ length: max }, (_, i) => ratings.filter((r) => r.stars === max - i).length);
  const top = Math.max(1, ...counts);
  return (
    <Stack spacing={0.6} sx={{ minWidth: 0, flex: 1 }} aria-label="How many people gave each rating">
      {counts.map((c, i) => {
        const starsHere = max - i;
        return (
          <Stack key={starsHere} direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color={ui.textSecondary} sx={{ width: 26, textAlign: "right", fontWeight: 700 }}>
              {starsHere}★
            </Typography>
            <Box sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: alpha(ui.textSecondary, 0.12), overflow: "hidden" }}>
              <Box sx={{ width: `${(c / top) * 100}%`, height: "100%", bgcolor: STAR, borderRadius: 4 }} />
            </Box>
            <Typography variant="caption" color={ui.textPrimary} sx={{ width: 18, fontWeight: 700 }}>
              {c}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  );
};

/** Averages by who gave them: management's view and the task team's view can differ. */
const ByCapacity = ({ ui, ratings }) => {
  const groups = [
    { label: "Top management", list: ratings.filter((r) => r.capacity === "top") },
    { label: "Leaders & supervisors", list: ratings.filter((r) => r.capacity === "leader" || r.capacity === "supervisor") },
    { label: "No longer on the task", list: ratings.filter((r) => r.capacity === "former") },
  ].filter((g) => g.list.length);
  if (groups.length < 2) return null;   // one group adds nothing to the overall figure
  return (
    <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1.5 }}>
      {groups.map((g) => (
        <Box key={g.label} sx={{ px: 1.2, py: 0.6, borderRadius: "10px", border: `1px solid ${ui.border}` }}>
          <Typography variant="caption" color={ui.textSecondary} display="block">
            {g.label}
          </Typography>
          <Typography variant="body2" fontWeight={800} color={ui.textPrimary}>
            {avg(g.list).toFixed(1)}{" "}
            <Typography component="span" variant="caption" color={ui.textSecondary}>
              ({g.list.length})
            </Typography>
          </Typography>
        </Box>
      ))}
    </Stack>
  );
};

/**
 * A rating to read, not click - for the performance report. A dash when
 * nothing was rated, which is not the same as a low rating.
 */
export const RatingBadge = ({ ui, average, count, variant = "body2" }) => {
  if (!count) {
    return (
      <Typography variant={variant} component="span" sx={{ color: ui.textSecondary }}>
        —
      </Typography>
    );
  }
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, whiteSpace: "nowrap" }}>
      <StarRoundedIcon aria-hidden="true" sx={{ fontSize: variant === "caption" ? 15 : 17, color: STAR }} />
      <Typography
        variant={variant}
        component="span"
        fontWeight={800}
        color={ui.textPrimary}
        sx={{ fontVariantNumeric: "tabular-nums" }}
      >
        {Number(average).toFixed(1)}
      </Typography>
      <Typography variant="caption" component="span" color={ui.textSecondary}>
        ({count})
      </Typography>
    </Box>
  );
};

/** A staff member's photo, or their initials. */
const PersonAvatar = ({ name, image, size }) => (
  <Avatar
    src={image ? `${STAFF_IMAGE_BASE}${image}` : undefined}
    alt={name || ""}
    sx={{ width: size, height: size, fontSize: Math.round(size * 0.38), flexShrink: 0 }}
  >
    {initials(String(name || "").split(" ")[0], String(name || "").split(" ").slice(-1)[0])}
  </Avatar>
);

/** One rating, with who gave it and in what capacity. */
export const RatingRow = ({ ui, r, max }) => {
  const photo = useStaffPhoto(r.rater_id, r.rater_image);
  return (
    <Stack direction="row" spacing={1.5} sx={{ py: 1.3 }}>
      <Avatar
        src={photo ? `${STAFF_IMAGE_BASE}${photo}` : undefined}
        alt={r.rater_name}
        sx={{ width: 34, height: 34, fontSize: 13 }}
      >
        {initials(r.rater_name.split(" ")[0], r.rater_name.split(" ").slice(-1)[0])}
      </Avatar>
      <Box flex={1} minWidth={0}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={0.5}>
          <Typography variant="body2" fontWeight={700} color={ui.textPrimary}>
            {r.rater_name}
            {r.mine ? " (you)" : ""}
          </Typography>
          {r.capacity && (
            <Box
              component="span"
              sx={{ px: 0.8, py: 0.1, borderRadius: "6px", fontSize: 11, fontWeight: 700, color: ui.textSecondary, border: `1px solid ${ui.border}` }}
            >
              {CAPACITY[r.capacity] || r.capacity}
            </Box>
          )}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.3 }}>
          <Rating value={r.stars} max={max} size="small" readOnly sx={starSx(ui)} />
          <Typography variant="caption" fontWeight={700} color={ui.textPrimary}>
            {r.stars} · {r.meaning}
          </Typography>
        </Stack>
        {r.comment && (
          <Typography variant="body2" color={ui.textPrimary} sx={{ mt: 0.6, whiteSpace: "pre-wrap" }}>
            {r.comment}
          </Typography>
        )}
        <Typography variant="caption" color={ui.textSecondary} display="block" sx={{ mt: 0.3 }}>
          {r.edited ? `Rated ${when(r.created_at)} · changed ${when(r.when)}` : `Rated ${when(r.when)}`}
        </Typography>
      </Box>
    </Stack>
  );
};

/** One subtask or daily report inside the task's report - opens its own report. */
const CrossRow = ({ ui, item, summary, onSelect }) => (
  <Box
    component="button"
    type="button"
    onClick={onSelect}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      width: "100%",
      textAlign: "left",
      font: "inherit",
      bgcolor: "transparent",
      border: 0,
      borderBottom: `1px solid ${alpha(ui.border, 0.6)}`,
      py: 1.1,
      px: 0.5,
      cursor: "pointer",
      "&:hover": { bgcolor: alpha(ui.textSecondary, 0.06) },
      "&:last-of-type": { borderBottom: 0 },
    }}
  >
    {item.person?.name && <PersonAvatar name={item.person.name} image={item.person.image} size={28} />}
    <Box flex={1} minWidth={0}>
      <Typography variant="body2" fontWeight={700} color={ui.textPrimary} noWrap>
        {item.title}
      </Typography>
      {item.subtitle && (
        <Typography variant="caption" color={ui.textSecondary} noWrap component="div">
          {item.subtitle}
        </Typography>
      )}
    </Box>
    {summary?.count ? (
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
        <StarRoundedIcon sx={{ fontSize: 17, color: STAR }} />
        <Typography variant="body2" fontWeight={800} color={ui.textPrimary}>
          {Number(summary.average).toFixed(1)}
        </Typography>
        <Typography variant="caption" color={ui.textSecondary}>
          ({summary.count})
        </Typography>
      </Stack>
    ) : (
      <Typography variant="caption" color={ui.textSecondary} sx={{ flexShrink: 0 }}>
        Not rated
      </Typography>
    )}
  </Box>
);

/** Pooled average over every rating on a set of items. */
const pooled = (items, getSummary) => {
  let n = 0;
  let sum = 0;
  let rated = 0;
  items.forEach((it) => {
    const s = getSummary(it.type, it.id);
    if (s?.count) {
      rated++;
      n += s.count;
      sum += s.ratings.reduce((t, r) => t + r.stars, 0);
    }
  });
  return { ratings: n, rated, average: n ? Math.round((sum / n) * 10) / 10 : null };
};

/* ================================================================== */
/*  The task's own rating card                                         */
/* ================================================================== */

export const TaskRatingCard = ({ ui, summary, meanings, max, onOpen, onRate }) => {
  if (!summary) return null;
  const { count, average, can_rate: canRate, why_not: whyNot, mine, ratings } = summary;
  const rounded = average ? Math.round(average) : 0;

  const buttonSx = {
    textTransform: "none",
    fontWeight: 700,
    borderRadius: "10px",
    color: ui.textPrimary,
    border: `1px solid ${ui.border}`,
    px: 1.5,
  };

  // Everyone who can see the task can open the report; rating is separate.
  const action = (
    <Stack direction="row" spacing={1}>
      <Button size="small" onClick={onOpen} sx={buttonSx}>
        Rating report
      </Button>
      {canRate && (
        <Button size="small" startIcon={<StarRoundedIcon />} onClick={onRate || onOpen} sx={buttonSx}>
          {mine ? "Change your rating" : "Rate this task"}
        </Button>
      )}
    </Stack>
  );

  return (
    <SectionCard
      ui={ui}
      title="Rating"
      hint="Whether the work actually solved the problem, rated by top management and the task's leaders and supervisors."
      action={action}
    >
      {count ? (
        <>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 40, fontWeight: 800, lineHeight: 1, color: ui.textPrimary }}>
              {Number(average).toFixed(1)}
            </Typography>
            <Box>
              <Rating value={average} max={max} precision={0.5} readOnly sx={starSx(ui)} />
              <Typography variant="body2" color={ui.textSecondary}>
                {meanings[rounded] || ""} · {count} rating{count === 1 ? "" : "s"}
              </Typography>
            </Box>
          </Stack>
          <Stack divider={<Divider sx={{ borderColor: alpha(ui.border, 0.6) }} />}>
            {ratings.slice(0, 3).map((r) => (
              <RatingRow key={r.rater_id} ui={ui} r={r} max={max} />
            ))}
          </Stack>
          {count > 3 && (
            <Button size="small" onClick={onOpen} sx={{ mt: 1, textTransform: "none" }}>
              See all {count} ratings
            </Button>
          )}
        </>
      ) : (
        <Typography variant="body2" color={ui.textSecondary}>
          {canRate ? "Not rated yet." : whyNot || "Not rated yet."}
        </Typography>
      )}
    </SectionCard>
  );
};

/* ================================================================== */
/*  The rating report: one item in full, and your own rating           */
/* ================================================================== */

/**
 * For any task, subtask or daily report: the average, how the stars spread,
 * management's view beside the team's, and every rating with who gave it,
 * in what capacity, and whether they changed it.
 *
 * For the task itself it also lists every subtask and daily report with its
 * own average; picking one opens that item's report, with a way back.
 *
 * Anyone who can see the task can read this. Rating is only offered to the
 * people the server says may rate this item.
 */
export const RatingDialog = ({
  open,
  ui,
  title,
  itemType,
  summary,
  meanings,
  max,
  crossItems,
  getSummary,
  onSelectItem,
  onBack,
  onClose,
  onSubmit,
  person,
}) => {
  const mine = summary?.mine || null;
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(-1);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Start from the person's existing rating each time a report opens.
  useEffect(() => {
    if (open) {
      setStars(mine?.stars || 0);
      setComment(mine?.comment || "");
      setError("");
      setHover(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, title]);

  const needsReason = stars > 0 && stars <= 2 && !comment.trim();
  const shown = hover !== -1 ? hover : stars;
  const ratings = useMemo(() => summary?.ratings || [], [summary]);
  const ordered = useMemo(() => (mine ? [mine, ...ratings.filter((r) => !r.mine)] : ratings), [mine, ratings]);

  const subtasks = (crossItems || []).filter((i) => i.type === "subtask");
  const reports = (crossItems || []).filter((i) => i.type === "progress");
  const subPool = getSummary ? pooled(subtasks, getSummary) : null;
  const repPool = getSummary ? pooled(reports, getSummary) : null;

  const save = async () => {
    if (!stars) {
      setError("Choose how many stars");
      return;
    }
    if (needsReason) {
      setError(`Say briefly what was missing - a rating of ${stars} needs a reason.`);
      return;
    }
    setSaving(true);
    setError("");
    const res = await onSubmit(stars, comment.trim());
    setSaving(false);
    if (!res?.success) {
      setError(res?.message || "Could not save the rating");
      return;
    }
    onClose(res);
  };

  const kind = itemType === "subtask" ? "subtask" : itemType === "progress" ? "daily report" : "task";

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      maxWidth="md"
      fullWidth
      scroll="paper"
      // The app's own surfaces, not MUI's default paper - which in dark mode
      // is a different grey with a lightening overlay.
      PaperProps={{
        sx: {
          bgcolor: ui.surface,
          backgroundImage: "none",
          color: ui.textPrimary,
          borderRadius: "16px",
          border: `1px solid ${ui.border}`,
          boxShadow: ui.shadow,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1.5, pr: 7 }}>
        {onBack && (
          <Button
            size="small"
            onClick={onBack}
            startIcon={<ArrowBackRoundedIcon sx={{ fontSize: "18px !important" }} />}
            sx={{ textTransform: "none", fontWeight: 700, px: 0.5, mb: 0.5, minWidth: 0, color: ui.accent }}
          >
            Back to the task report
          </Button>
        )}
        <Typography variant="overline" color={ui.textSecondary} display="block" sx={{ lineHeight: 1.6, fontWeight: 700 }}>
          Rating report · {kind}
        </Typography>
        <Typography variant="h6" fontWeight={800} color={ui.textPrimary} sx={{ lineHeight: 1.3 }}>
          {title}
        </Typography>
        {/* Whose work this is: the report's author, or the subtask's owner. */}
        {person?.name && itemType !== "task" && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
            <PersonAvatar name={person.name} image={person.image} size={30} />
            <Typography variant="body2" color={ui.textSecondary}>
              {itemType === "progress" ? "Written by" : "Accountable"}{" "}
              <Typography component="span" variant="body2" fontWeight={700} color={ui.textPrimary}>
                {person.name}
              </Typography>
            </Typography>
          </Stack>
        )}
        <IconButton
          aria-label="Close"
          onClick={() => !saving && onClose()}
          sx={{ position: "absolute", right: 12, top: 12, color: ui.textSecondary }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: ui.border, bgcolor: ui.surface }}>
        {/* ---------- Your rating ---------- */}
        {summary?.can_rate ? (
          <Box sx={{ p: 2, mb: 2.5, borderRadius: "12px", border: `1px solid ${ui.border}`, bgcolor: ui.surfaceAlt }}>
            <Typography variant="subtitle2" fontWeight={800} color={ui.textPrimary}>
              {mine ? "Your rating" : "Did this work solve the problem?"}
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
              <Rating
                name="task-rating"
                value={stars}
                max={max}
                size="large"
                onChange={(_, v) => setStars(v || 0)}
                onChangeActive={(_, v) => setHover(v)}
                getLabelText={(v) => `${v} of ${max}: ${meanings[v] || ""}`}
                sx={starSx(ui)}
              />
              <Typography variant="body2" fontWeight={700} color={ui.textPrimary} sx={{ minWidth: 0 }}>
                {shown > 0 ? meanings[shown] : ""}
              </Typography>
            </Stack>
            <TextField
              fullWidth
              multiline
              minRows={2}
              size="small"
              label={stars > 0 && stars <= 2 ? "What was missing? (required)" : "Comment (optional)"}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              inputProps={{ maxLength: 2000 }}
              error={needsReason}
              sx={{
                mt: 1.5,
                "& .MuiOutlinedInput-root": {
                  bgcolor: ui.surface,
                  borderRadius: "10px",
                  color: ui.textPrimary,
                  "& fieldset": { borderColor: ui.border },
                  "&:hover fieldset": { borderColor: ui.accent },
                  "&.Mui-focused fieldset": { borderColor: ui.accent },
                },
                "& .MuiInputLabel-root": { color: ui.textSecondary },
                "& .MuiInputLabel-root.Mui-focused": { color: ui.accent },
              }}
            />
            {error && (
              <Alert severity="warning" sx={{ mt: 1.5, borderRadius: "10px" }}>
                {error}
              </Alert>
            )}
          </Box>
        ) : (
          summary?.why_not && (
            <Alert severity="info" sx={{ mb: 2.5, borderRadius: "10px" }}>
              {summary.why_not}
            </Alert>
          )
        )}

        {/* ---------- Summary ---------- */}
        {summary?.count ? (
          <Box sx={{ p: 2, mb: 2, borderRadius: "12px", border: `1px solid ${ui.border}` }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={3} alignItems={{ xs: "stretch", sm: "center" }}>
              <Box sx={{ minWidth: 150 }}>
                <Typography sx={{ fontSize: 44, fontWeight: 800, lineHeight: 1, color: ui.textPrimary }}>
                  {Number(summary.average).toFixed(1)}
                  <Typography component="span" variant="body2" color={ui.textSecondary} sx={{ ml: 0.5 }}>
                    / {max}
                  </Typography>
                </Typography>
                <Rating value={summary.average} max={max} precision={0.5} readOnly sx={starSx(ui, { mt: 0.5 })} />
                <Typography variant="body2" color={ui.textSecondary}>
                  {meanings[Math.round(summary.average)] || ""}
                </Typography>
                <Typography variant="caption" color={ui.textSecondary}>
                  {summary.count} rating{summary.count === 1 ? "" : "s"}
                </Typography>
              </Box>
              <Breakdown ui={ui} ratings={ratings} max={max} />
            </Stack>
            <ByCapacity ui={ui} ratings={ratings} />
          </Box>
        ) : (
          <Typography variant="body2" color={ui.textSecondary} sx={{ mb: 2 }}>
            This {kind} has not been rated yet.
          </Typography>
        )}

        {/* ---------- Every rating ---------- */}
        {ordered.length > 0 && (
          <>
            <Typography variant="subtitle2" fontWeight={800} color={ui.textPrimary}>
              Every rating
            </Typography>
            <Stack divider={<Divider sx={{ borderColor: alpha(ui.border, 0.6) }} />} sx={{ mb: 2 }}>
              {ordered.map((r) => (
                <RatingRow key={r.rater_id} ui={ui} r={r} max={max} />
              ))}
            </Stack>
          </>
        )}

        {/* ---------- Across the task ---------- */}
        {itemType === "task" && getSummary && (subtasks.length > 0 || reports.length > 0) && (
          <Box sx={{ mt: 1 }}>
            <Divider sx={{ borderColor: ui.border, mb: 2 }} />
            <Typography variant="subtitle2" fontWeight={800} color={ui.textPrimary}>
              Across this task
            </Typography>
            <Typography variant="caption" color={ui.textSecondary} display="block" sx={{ mb: 1.5 }}>
              Every subtask and daily report, with its own rating. Choose one for its full report.
            </Typography>

            {subtasks.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                  <Typography variant="body2" fontWeight={800} color={ui.textPrimary}>
                    Subtasks
                  </Typography>
                  <Typography variant="caption" color={ui.textSecondary}>
                    {subPool.ratings
                      ? `Average ${subPool.average.toFixed(1)} from ${subPool.ratings} rating${subPool.ratings === 1 ? "" : "s"} · ${subPool.rated} of ${subtasks.length} rated`
                      : `None of ${subtasks.length} rated yet`}
                  </Typography>
                </Stack>
                {subtasks.map((it) => (
                  <CrossRow key={`s${it.id}`} ui={ui} item={it} summary={getSummary("subtask", it.id)} onSelect={() => onSelectItem(it)} />
                ))}
              </Box>
            )}

            {reports.length > 0 && (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                  <Typography variant="body2" fontWeight={800} color={ui.textPrimary}>
                    Daily reports
                  </Typography>
                  <Typography variant="caption" color={ui.textSecondary}>
                    {repPool.ratings
                      ? `Average ${repPool.average.toFixed(1)} from ${repPool.ratings} rating${repPool.ratings === 1 ? "" : "s"} · ${repPool.rated} of ${reports.length} rated`
                      : `None of ${reports.length} rated yet`}
                  </Typography>
                </Stack>
                {reports.map((it) => (
                  <CrossRow key={`p${it.id}`} ui={ui} item={it} summary={getSummary("progress", it.id)} onSelect={() => onSelectItem(it)} />
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={() => onClose()} disabled={saving} sx={{ textTransform: "none", color: ui.textSecondary }}>
          {summary?.can_rate ? "Cancel" : "Close"}
        </Button>
        {summary?.can_rate && (
          <Button
            onClick={save}
            disabled={saving || !stars}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <StarRoundedIcon />}
            sx={{
              background: ui.gradient,
              color: "#fff",
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 700,
              px: 2.5,
              "&.Mui-disabled": { opacity: 0.55, color: "#fff" },
            }}
          >
            {mine ? "Update rating" : "Save rating"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
